import React, { useCallback, useEffect, useId, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AddFundsModal from "../margin/AddFundsModal";
import StakingService from "../../../api/services/StakingService";
import { findFundRowBySymbol } from "../WithdrawPage/withdrawPageHelpers";
import { alertSuccessMessage } from "../../../customComponents/CustomAlertMessage";
import StakeConfirmOverview from "./StakeConfirmOverview";
import StakeSuccessPopup from "./StakeSuccessPopup";
import {
  formatDailyReturn,
  formatStakeBalance,
  formatTierRange,
  getBonusFirstDistributeDate,
  getPackageCurrencyId,
  getPackageIcon,
  getRewardIcon,
  getReturnAccruesLabel,
  getStakeAmountError,
  getTotalInvestedAmount,
  getUnbondingPeriodDays,
  parseDec,
  resolveStakeWalletBalance,
} from "./stakingStakeHelpers";
import "./staking.css";

const resolvePositionPackageId = (position) => {
  const pkg = position?.packageId;
  if (!pkg) return null;
  if (typeof pkg === "string") return pkg;
  return pkg._id || pkg.packageId || null;
};

const StakeModal = ({
  selectedPackage,
  positionId = null,
  currentStakingAmount,
  onClose,
  onSuccess,
  onTransferClick,
  requireOverviewConfirm = false,
  balanceCache = null,
}) => {
  const isTopUp = Boolean(positionId);
  const agreeId = useId();

  /** Read a prefetched Available balance (set on the Staking page when it loads) so the modal shows it instantly. */
  const readCachedBalance = useCallback(
    (pkg) => {
      if (!balanceCache || !pkg) return null;
      const cid = getPackageCurrencyId(pkg);
      if (cid != null && balanceCache.has(String(cid))) return balanceCache.get(String(cid));
      const sym = String(pkg?.currency || "").toUpperCase();
      if (sym && balanceCache.has(sym)) return balanceCache.get(sym);
      return null;
    },
    [balanceCache],
  );

  const [stakeAmount, setStakeAmount] = useState("");
  const [stakeChecked, setStakeChecked] = useState(false);
  const [stakeLoading, setStakeLoading] = useState(false);
  const [stakeError, setStakeError] = useState("");
  const [stakeWalletBalance, setStakeWalletBalance] = useState(() => readCachedBalance(selectedPackage));
  const [stakeBalanceLoading, setStakeBalanceLoading] = useState(false);
  const [resolvedCurrentStaking, setResolvedCurrentStaking] = useState(null);
  const [currentStakingLoading, setCurrentStakingLoading] = useState(false);
  const [addFundsOpen, setAddFundsOpen] = useState(false);
  const [showOverviewConfirm, setShowOverviewConfirm] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [overviewError, setOverviewError] = useState("");

  const fetchStakeWalletBalance = useCallback(async (pkg) => {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    if (!token || !pkg) {
      setStakeWalletBalance(null);
      return;
    }

    // Show the prefetched value immediately (if any) and skip the loading flash.
    const cached = readCachedBalance(pkg);
    if (cached != null) setStakeWalletBalance(cached);

    let currencyId = getPackageCurrencyId(pkg);
    if (!currencyId) {
      const walletRes = await StakingService.getEarningWallet();
      if (walletRes?.success && Array.isArray(walletRes.data)) {
        const row = findFundRowBySymbol(walletRes.data, pkg.currency);
        currencyId = row?.currency_id ?? row?.currencyId ?? null;
      }
    }

    if (!currencyId) {
      if (cached == null) setStakeWalletBalance("0");
      return;
    }

    if (cached == null) setStakeBalanceLoading(true);
    try {
      const res = await StakingService.getUserBalance(currencyId, "earning");
      const resolved = resolveStakeWalletBalance(res);
      setStakeWalletBalance(resolved);
      if (balanceCache) {
        balanceCache.set(String(currencyId), resolved);
        const sym = String(pkg?.currency || "").toUpperCase();
        if (sym) balanceCache.set(sym, resolved);
      }
    } catch {
      if (cached == null) setStakeWalletBalance("0");
    } finally {
      setStakeBalanceLoading(false);
    }
  }, [balanceCache, readCachedBalance]);

  const fetchCurrentStaking = useCallback(async (pkg) => {
    setCurrentStakingLoading(true);
    try {
      const res = await StakingService.getMyPositions(1, 100);
      if (res?.success && Array.isArray(res.data)) {
        const selectedId = pkg?._id || pkg?.packageId;
        const currency = String(pkg?.currency || "").toUpperCase();
        const activeMatches = res.data.filter((position) => {
          if (String(position?.status || "").toUpperCase() !== "ACTIVE") return false;
          const posPkgId = resolvePositionPackageId(position);
          if (selectedId && posPkgId && String(posPkgId) === String(selectedId)) return true;
          return String(position.currency || "").toUpperCase() === currency;
        });
        if (activeMatches.length === 0) {
          setResolvedCurrentStaking("0");
        } else {
          const total = activeMatches.reduce((sum, position) => {
            const n = parseFloat(parseDec(getTotalInvestedAmount(position))) || 0;
            return sum + n;
          }, 0);
          setResolvedCurrentStaking(String(total));
        }
      } else {
        setResolvedCurrentStaking("0");
      }
    } catch {
      setResolvedCurrentStaking("0");
    } finally {
      setCurrentStakingLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!selectedPackage) return undefined;
    setStakeAmount("");
    setStakeChecked(false);
    setStakeError("");
    setOverviewError("");
    setShowOverviewConfirm(false);
    setShowSuccessPopup(false);
    setStakeWalletBalance(readCachedBalance(selectedPackage));
    setResolvedCurrentStaking(null);
    fetchStakeWalletBalance(selectedPackage);
    if (currentStakingAmount == null) {
      fetchCurrentStaking(selectedPackage);
    }
    return undefined;
  }, [selectedPackage, currentStakingAmount, fetchStakeWalletBalance, fetchCurrentStaking, readCachedBalance]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    };
  }, []);

  const stakeAvailableNum = parseFloat(parseDec(stakeWalletBalance)) || 0;
  const displayCurrentStaking = currentStakingAmount != null
    ? parseDec(currentStakingAmount)
    : (resolvedCurrentStaking ?? "0");

  const stakeAmountValidationError = useMemo(() => {
    if (!selectedPackage || !stakeAmount) return null;
    return getStakeAmountError(stakeAmount, selectedPackage, stakeAvailableNum, stakeWalletBalance, {
      skipMaxCheck: isTopUp,
    });
  }, [stakeAmount, selectedPackage, stakeAvailableNum, stakeWalletBalance, isTopUp]);

  const estimatedDailyReturn = useMemo(() => {
    const amount = parseFloat(stakeAmount) || 0;
    const returnPercentage = parseFloat(selectedPackage?.returnPercentage) || 0;
    if (!amount || !returnPercentage) return "0";
    const dailyReturn = (amount * returnPercentage / 100) / 365;
    return formatDailyReturn(dailyReturn);
  }, [stakeAmount, selectedPackage]);

  const handleMaxStake = () => {
    if (!selectedPackage) return;
    const available = stakeAvailableNum;
    if (isTopUp) {
      setStakeAmount(available > 0 ? String(available) : "");
      return;
    }
    const packageMax = Number(selectedPackage.maxAmount);
    const maxStake = Number.isFinite(packageMax) ? Math.min(available, packageMax) : available;
    setStakeAmount(maxStake > 0 ? String(maxStake) : "");
  };

  const submitStake = async () => {
    const amount = Number(stakeAmount);
    setStakeLoading(true);
    setOverviewError("");
    try {
      const res = isTopUp
        ? await StakingService.topUp(positionId, amount, "earning")
        : await StakingService.subscribe(selectedPackage._id || selectedPackage.packageId, amount, "earning");
      if (res?.success) {
        if (requireOverviewConfirm) {
          setShowOverviewConfirm(false);
          setShowSuccessPopup(true);
          setOverviewError("");
        } else {
          alertSuccessMessage(
            res?.message || (isTopUp ? "taking subscription successful." : "Staking subscription successful.")
          );
          onSuccess?.();
          onClose();
        }
      } else {
        const message = res?.message || (isTopUp ? "Top up failed. Please try again." : "Staking failed. Please try again.");
        if (showOverviewConfirm) {
          setOverviewError(message);
        } else {
          setStakeError(message);
        }
      }
    } catch {
      const message = "Something went wrong. Please try again.";
      if (showOverviewConfirm) {
        setOverviewError(message);
      } else {
        setStakeError(message);
      }
    } finally {
      setStakeLoading(false);
    }
  };

  const handleConfirm = async () => {
    setStakeError("");
    setOverviewError("");
    const validationError = getStakeAmountError(
      stakeAmount,
      selectedPackage,
      stakeAvailableNum,
      stakeWalletBalance,
      { skipMaxCheck: isTopUp }
    );
    if (validationError) {
      setStakeError(validationError);
      return;
    }

    if (requireOverviewConfirm) {
      setShowOverviewConfirm(true);
      return;
    }

    await submitStake();
  };

  const handleOverviewBack = () => {
    if (stakeLoading) return;
    setShowOverviewConfirm(false);
    setOverviewError("");
  };

  const handleSuccessDone = () => {
    setShowSuccessPopup(false);
    onSuccess?.();
    onClose();
  };

  if (!selectedPackage) return null;

  return (
    <>
      <div className="mint-modal-overlay" onClick={onClose}>
        <div className="mint-modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="mint-modal-header">
            <div className="mint-modal-title">
              <img
                src={getPackageIcon(selectedPackage)}
                alt="icon"
                className="mint-title-icon"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = `/images/staking_img/${String(selectedPackage.currency || "usdt").toLowerCase()}_icon.svg`;
                }}
              />
              <span>{selectedPackage.currency} Staking</span>
            </div>
            <button type="button" className="mint-modal-close" onClick={onClose}>
              <i className="ri-close-line" />
            </button>
          </div>

          <div className="mint-modal-body">
            <div className="mint-modal-left">
              <div className="mint-info-row">
                <div className="mint-info-item">
                  <span className="mint-info-label">Protocol</span>
                  <span className="mint-info-value">{selectedPackage.currency}</span>
                </div>
                <div className="mint-info-item mint-info-item-right">
                  <span className="mint-info-label">Est. APR <i className="ri-line-chart-line" /></span>
                  <span className="mint-info-value mint-green-text">{selectedPackage.returnPercentage}%</span>
                </div>
              </div>

              <div className="mint-amount-section">
                <label className="mint-section-label">Amount</label>
                <div className="mint-input-box">
                  <input
                    type="number"
                    placeholder={`Min. ${selectedPackage.minAmount ?? 0}`}
                    value={stakeAmount}
                    onChange={(e) => setStakeAmount(e.target.value)}
                  />
                  <div className="mint-input-addon">
                    <span className="mint-input-currency">{selectedPackage.currency}</span>
                    <div className="mint-input-separator" />
                    <span className="mint-max-btn" onClick={handleMaxStake}>Max</span>
                  </div>
                </div>
                {stakeAmountValidationError && (
                  <div className="mint-error-msg">
                    {stakeAmountValidationError}
                  </div>
                )}
                <div className="mint-amount-details">
                  <div className="mint-amount-row">
                    <span className="mint-amount-lbl mint-amount-lbl--inline">
                      Available
                      <span className="mint-tooltip-wrapper mint-amount-info-tooltip">
                        <i className="ri-information-line"></i>                        <span className="mint-tooltip">Funds will be deducted from your Earning Wallet.</span>
                      </span>
                    </span>
                    <span className="mint-amount-val">
                      {stakeBalanceLoading ? "Loading..." : `${formatStakeBalance(stakeWalletBalance)} ${selectedPackage.currency}`}
                      <i className="ri-add-circle-fill mint-add-icon" onClick={() => setAddFundsOpen(true)} />
                    </span>
                  </div>
                  <div className="mint-amount-row">
                    <span className="mint-amount-lbl">Your current staking</span>
                    <span className="mint-amount-val">
                      {currentStakingLoading
                        ? "Loading..."
                        : `${formatStakeBalance(displayCurrentStaking)} ${selectedPackage.currency}`}
                    </span>
                  </div>
                </div>
              </div>

              <div className="mint-apr-section">
                <label className="mint-section-label">Est. APR</label>
                <div className="mint-apr-box mint-apr-box-col">
                  <div className="mint-apr-standard-row">
                    <span>Standard APR</span>
                    <span className="mint-apr-value">{selectedPackage.returnPercentage}%</span>
                  </div>
                  {Array.isArray(selectedPackage.bonusAprTiers) && selectedPackage.bonusAprTiers.length > 0 ? (
                    <>
                      <div className="mint-bonus-apr-label">Bonus APR</div>
                      <div className="tier-timeline">
                        {selectedPackage.bonusAprTiers.map((tier) => (
                          <div className="tier-item" key={tier._id || tier.tierName}>
                            <div className="tier-item-left">
                              <i className="ri-checkbox-circle-fill" /> {tier.tierName}: {formatTierRange(tier)}
                            </div>
                            <span>{Number(tier.bonusApr).toFixed(2)}%</span>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="mint-modal-right">
              <div className="mint-daily-return">
                <div className="mint-daily-return-header">
                  <span>Est. Daily Return</span>
                  <i className="ri-arrow-up-s-fill mint-arrow-icon" />
                </div>
                <div className="mint-daily-return-box mint-daily-return-col">
                  <div className="mint-daily-return-row">
                    <img
                      src={getRewardIcon(selectedPackage)}
                      alt="icon"
                      className="mint-return-icon"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = `/images/staking_img/${String(selectedPackage.rewardCurrency || selectedPackage.currency || "usdt").toLowerCase()}_icon.svg`;
                      }}
                    />
                    <span className="mint-return-val">
                      {estimatedDailyReturn} {selectedPackage.rewardCurrency || selectedPackage.currency}
                    </span>
                  </div>
                </div>
              </div>

              <div className="mint-rules-section">
                <div className="mint-rules-header">
                  <div className="mint-rules-title">Trading Rules <div className="mint-title-underline" /></div>
                </div>

                <div className="mint-timeline">
                  <div className="mint-timeline-item">
                    <div className="mint-timeline-dot active" />
                    <div className="mint-timeline-content">
                      <span className="mint-timeline-lbl">Staking Time</span>
                      <span className="mint-timeline-val">Now</span>
                    </div>
                  </div>
                  <div className="mint-timeline-item">
                    <div className="mint-timeline-dot" />
                    <div className="mint-timeline-content">
                      <span className="mint-timeline-lbl">Return Accrues</span>
                      <span className="mint-timeline-val">{getReturnAccruesLabel(selectedPackage.interestStartsAfterDays)}</span>
                    </div>
                  </div>
                  <div className="mint-timeline-item">
                    <div className="mint-timeline-dot" />
                    <div className="mint-timeline-content">
                      <span className="mint-timeline-lbl">{selectedPackage.rewardCurrency || selectedPackage.currency} Distributes</span>
                      <span className="mint-timeline-val">Daily</span>
                    </div>
                  </div>
                  <div className={`mint-timeline-item${!(Array.isArray(selectedPackage.bonusAprTiers) && selectedPackage.bonusAprTiers.length > 0) ? " last" : ""}`}>
                    <div className="mint-timeline-dot" />
                    <div className="mint-timeline-content">
                      <span className="mint-timeline-lbl">Unbonding Period</span>
                      <div className="mint-timeline-val multi-line">
                        About {getUnbondingPeriodDays(selectedPackage)} day(s)
                      </div>
                    </div>
                  </div>
                  {Array.isArray(selectedPackage.bonusAprTiers) && selectedPackage.bonusAprTiers.length > 0 ? (
                    <div className="mint-timeline-item last">
                      <div className="mint-timeline-dot" />
                      <div className="mint-timeline-content">
                        <span className="mint-timeline-lbl">Bonus Distributes (First Time)</span>
                        <span className="mint-timeline-val">{getBonusFirstDistributeDate()}</span>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>

              {selectedPackage.earlyWithdrawalPenalty > 0 ? (
                <div className="service-fee-container">
                  <div className="service-fee-header">
                    <span>Early Withdrawal Penalty</span>
                    <span className="service-fee-value">{selectedPackage.earlyWithdrawalPenalty}%</span>
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          <div className="mint-modal-footer">
            <div className="mint-agreement">
              <input type="checkbox" id={agreeId} checked={stakeChecked} onChange={(e) => setStakeChecked(e.target.checked)} />
              <label htmlFor={agreeId}>I have read and accepted the <Link to="/staking-user-agreement" onClick={(e) => e.stopPropagation()}>Staking User Agreement</Link></label>
            </div>
            {stakeError && (
              <div className="mint-error-msg mint-error-msg-center">
                {stakeError}
              </div>
            )}
            <div className="mint-actions">
              <button type="button" className="mint-btn-cancel" disabled={stakeLoading} onClick={onClose}>Cancel</button>
              <button
                type="button"
                className="mint-btn-confirm"
                disabled={stakeLoading || !stakeChecked || !stakeAmount || !!stakeAmountValidationError}
                onClick={handleConfirm}
              >
                {stakeLoading ? "Processing..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <AddFundsModal open={addFundsOpen} onClose={() => setAddFundsOpen(false)} onTransferClick={onTransferClick} />

      {showOverviewConfirm && (
        <StakeConfirmOverview
          selectedPackage={selectedPackage}
          stakeAmount={stakeAmount}
          estimatedDailyReturn={estimatedDailyReturn}
          currentStakingAmount={displayCurrentStaking}
          isTopUp={isTopUp}
          loading={stakeLoading}
          error={overviewError}
          onBack={handleOverviewBack}
          onConfirm={submitStake}
        />
      )}

      {showSuccessPopup && (
        <StakeSuccessPopup
          selectedPackage={selectedPackage}
          stakeAmount={stakeAmount}
          isTopUp={isTopUp}
          onDone={handleSuccessDone}
        />
      )}
    </>
  );
};

export default StakeModal;
